import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./keys";

import { filterOrgs, FilterOrgsRequest } from "~/functions/orgs/filterOrgs";
import { getOrg } from "~/functions/orgs/getOrg";
import { listAdminOrgs } from "~/functions/orgs/listAdminOrgs";
import {
  syncOrgToDaas,
  SyncOrgToDaasRequest,
} from "~/functions/orgs/syncOrgToDaas";
import {
  createOrganization,
  CreateOrganizationRequest,
} from "~/functions/orgs/createOrganization";
import {
  updateOrganizationFn,
  UpdateOrganizationRequest,
} from "~/functions/orgs/updateOrganization";
import {
  deleteOrganization,
  DeleteOrganizationRequest,
} from "~/functions/orgs/deleteOrganization";

import { QueryOptions } from "./types";

import { QueryNotificationsRequest } from "../abodemail/types";

// Query key generators (using our centralized keys)
export const orgQueryKeys = {
  all: () => queryKeys.admin.orgs.all,
  list: (params: {}) =>
    queryKeys.admin.orgs.list(params as Record<string, unknown>),
  detail: (id: string) => queryKeys.admin.orgs.detail(id),
  keys: (orgId: string) => queryKeys.admin.orgs.keys(orgId),
  quotas: (orgId: string) => queryKeys.admin.orgs.quotas(orgId),
  communications: (orgId?: string, params?: QueryNotificationsRequest) => [
    "orgs",
    "communications",
    orgId,
    params,
  ],
  adminList: () => [...queryKeys.admin.orgs.all, "reconciled"] as const,
};

// // Export query key invalidation helpers
// export const invalidateOrgQueries = {
//   all: () => queryKeys.admin.orgs.all,
//   lists: () => queryKeys.admin.orgs.lists(),
//   org: (id: string) => [
//     queryKeys.admin.orgs.detail(id),
//     queryKeys.admin.orgs.keys(id),
//     queryKeys.admin.orgs.quotas(id),
//   ],
// }

// Organization list hook
export function useOrganizations(params: FilterOrgsRequest = {sortOrder: "asc"}, options: QueryOptions = {}) {
  return useQuery({
    queryKey: orgQueryKeys.list(params),
    queryFn: () => filterOrgs({data: params}),
    staleTime: options.staleTime ?? 5 * 60 * 1000, // 5 minutes
    gcTime: options.gcTime ?? 10 * 60 * 1000, // 10 minutes
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
  });
}

// The admin list: Entitlements' dde holders reconciled against DaaS's own orgs.
export function useAdminOrgList(options: QueryOptions = {}) {
  return useQuery({
    queryKey: orgQueryKeys.adminList(),
    queryFn: () => listAdminOrgs(),
    staleTime: options.staleTime ?? 5 * 60 * 1000,
    gcTime: options.gcTime ?? 10 * 60 * 1000,
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
  });
}

// Registers an Entitlements organization into DaaS under the same id.
export function useSyncOrgToDaas() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SyncOrgToDaasRequest) => syncOrgToDaas({ data }),
    onSuccess: (org) => {
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.adminList() });
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.list({}) });
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.detail(org.id) });
    },
  });
}

// Single organization hook
export function useOrganization(
  id: string | null | undefined,
  options: QueryOptions = {},
) {
  return useQuery({
    queryKey: orgQueryKeys.detail(id!),
    queryFn: () =>
      getOrg({
        data: id!,
      }),
    enabled: Boolean(id) && (options.enabled ?? true),
    staleTime: options.staleTime ?? 5 * 60 * 1000,
    gcTime: options.gcTime ?? 10 * 60 * 1000,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
  });
}

// Create organization mutation
export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganizationRequest) =>
      createOrganization({ data }),
    onSuccess: () => {
      // Invalidate all organization lists
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.list({}) });
    },
  });
}

// Update organization mutation
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateOrganizationRequest) =>
      updateOrganizationFn({ data }),
    onSuccess: (updatedOrg) => {
      // Invalidate all organization lists and the specific organization
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.list({}) });
      queryClient.invalidateQueries({
        queryKey: orgQueryKeys.detail(updatedOrg.id),
      });
    },
  });
}

// Delete organization mutation
export function useDeleteOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeleteOrganizationRequest) =>
      deleteOrganization({ data }),
    onSuccess: () => {
      // Invalidate all organization lists since the organization is deleted
      queryClient.invalidateQueries({ queryKey: orgQueryKeys.list({}) });
    },
  });
}

// export function useOrganizationCommunications(
//   request: GetOrganizationCommunicationsRequest,
//   options: QueryOptions = {},
// ) {
//   return useQuery({
//     queryKey: orgQueryKeys.communications(request.organization_id, {
//       ...request,
//       organization_id: undefined,
//     } as any),
//     queryFn: () => getOrganizationCommunications({ data: request }),
//     enabled: Boolean(request.organization_id) && (options.enabled ?? true),
//     staleTime: options.staleTime ?? 5 * 60 * 1000,
//     gcTime: options.gcTime ?? 10 * 60 * 1000,
//     refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
//   });
// }
